import axios from 'axios';
import { httpApi } from './http';
import type { RewardsV1ListParams, RewardsV1ListResponse, RewardsV1GetByIdResponse } from '../types/rewardsV1';
import type { RewardsV1SearchResponse } from '../types/rewardSearchV1';

export class RewardsV1Service {
  static async list(params: RewardsV1ListParams): Promise<RewardsV1ListResponse> {
    try {
      const response = await httpApi.get<RewardsV1ListResponse>('/rewardsv1', { params });
      if (!response.data) throw new Error('No data received from server');
      if (!response.data.success) throw new Error(response.data.error || 'API request failed');
      return response.data;
    } catch (error) {
      console.error('Error listing rewards v1:', error);
      if (axios.isAxiosError(error)) {
        throw new Error((error.response?.data as any)?.message || 'Failed to load rewards v1');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  static async legacySearch(query: string, opts?: { cardId?: string; questionId?: string; amount?: string | number }): Promise<RewardsV1SearchResponse> {
    try {
      const response = await httpApi.get<RewardsV1SearchResponse>('/rewardsv1/search', {
        params: {
          q: query,
          ...(opts?.cardId ? { cardId: opts.cardId } : {}),
          ...(opts?.questionId ? { questionId: opts.questionId } : {}),
          ...(opts?.amount != null && opts.amount !== '' ? { amount: opts.amount } : {}),
        },
      });

      if (!response.data) throw new Error('No data received from server');
      if (!response.data.success) throw new Error(response.data.error || 'API request failed');
      return response.data;
    } catch (error) {
      console.error('Error legacy-searching rewards v1:', error);
      if (axios.isAxiosError(error)) {
        throw new Error((error.response?.data as any)?.message || 'Failed to search rewards v1');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  static async getById(id: string): Promise<RewardsV1GetByIdResponse> {
    try {
      const response = await httpApi.get<RewardsV1GetByIdResponse>(`/rewardsv1/${encodeURIComponent(id)}`);
      if (!response.data) throw new Error('No data received from server');
      if (!response.data.success) throw new Error(response.data.error || 'API request failed');
      return response.data;
    } catch (error) {
      console.error('Error fetching reward v1 by id:', error);
      if (axios.isAxiosError(error)) {
        throw new Error((error.response?.data as any)?.message || 'Failed to fetch reward v1');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  static async updateById(id: string, payload: unknown): Promise<RewardsV1GetByIdResponse> {
    try {
      const response = await httpApi.put<RewardsV1GetByIdResponse>(`/rewardsv1/${encodeURIComponent(id)}`, payload);
      if (!response.data) throw new Error('No data received from server');
      if (!response.data.success) throw new Error(response.data.error || 'API request failed');
      return response.data;
    } catch (error) {
      console.error('Error updating reward v1 by id:', error);
      if (axios.isAxiosError(error)) {
        throw new Error((error.response?.data as any)?.message || 'Failed to update reward v1');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  static async updateEditById(id: string, payload: unknown): Promise<RewardsV1GetByIdResponse> {
    try {
      const response = await httpApi.put<RewardsV1GetByIdResponse>(
        `/rewardsv1/edit/${encodeURIComponent(id)}`,
        payload
      );
      if (!response.data) throw new Error('No data received from server');
      if (!response.data.success) throw new Error(response.data.error || 'API request failed');
      return response.data;
    } catch (error) {
      console.error('Error updating reward v1 (edit) by id:', error);
      if (axios.isAxiosError(error)) {
        throw new Error((error.response?.data as any)?.message || 'Failed to update reward v1');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  static async upsertNewById(id: string, payload: unknown): Promise<RewardsV1GetByIdResponse> {
    try {
      const response = await httpApi.put<RewardsV1GetByIdResponse>(
        `/rewardsv1/new/${encodeURIComponent(id)}`,
        payload
      );
      if (!response.data) throw new Error('No data received from server');
      if (!response.data.success) throw new Error(response.data.error || 'API request failed');
      return response.data;
    } catch (error) {
      console.error('Error upserting reward v1 (new) by id:', error);
      if (axios.isAxiosError(error)) {
        throw new Error((error.response?.data as any)?.message || 'Failed to save reward v1 (new)');
      }
      throw new Error('An unexpected error occurred');
    }
  }
}
